import React, { useState, useEffect } from 'react';
import { Phone, Clock, Play, FileText, ChevronDown, ChevronUp, Calendar, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { parseISO, isValid } from 'date-fns';
import { formatInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';

interface InlineCallTimelineProps {
  leadId: string;
  leadName?: string;
}

const InlineCallTimeline: React.FC<InlineCallTimelineProps> = ({ leadId, leadName = 'Lead' }) => {
  const tz = useTimezone();
  const [callLogs, setCallLogs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedTranscripts, setExpandedTranscripts] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;

    const fetchCallLogs = async () => {
      setIsLoading(true);
      const { data: logs, error } = await supabase
        .from('call_logs')
        .select('*')
        .eq('lead_id', leadId)
        .order('created_at', { ascending: false });

      if (!cancelled) {
        if (!error) setCallLogs(logs || []);
        setIsLoading(false);
      }
    };

    fetchCallLogs();
    return () => { cancelled = true; };
  }, [leadId]);

  const formatDateTime = (timestamp: string) => {
    if (!timestamp || timestamp === 'N/A') return 'Unknown time';
    try {
      const date = parseISO(timestamp);
      if (!isValid(date)) return 'Invalid date';
      return formatInTz(date, 'MMM dd, yyyy - HH:mm', tz);
    } catch {
      return 'Invalid date';
    }
  };

  const getCallOutcome = (outcome: string) => {
    if (!outcome || outcome === 'N/A') return null;
    const outcomeColors: Record<string, string> = {
      'connected': 'bg-[#05E0D1]/10 text-[#0bb5a5] border-[#05E0D1]/25',
      'not connected': 'bg-rose-500/10 text-rose-700 border-rose-500/20',
      'voicemail': 'bg-amber-500/10 text-amber-700 border-amber-500/20',
      'busy': 'bg-[#1875FF]/8 text-[#1875FF] border-[#1875FF]/20',
      'no answer': 'bg-gray-100 text-gray-600 border-gray-200',
    };
    const colorClass = outcomeColors[outcome.toLowerCase()] || 'bg-gray-100 text-gray-600 border-gray-200';
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold border ${colorClass}`}>
        {outcome}
      </span>
    );
  };

  const toggleTranscript = (index: number) => {
    setExpandedTranscripts(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <Loader2 size={24} className="mx-auto text-[#1875FF] mb-3 animate-spin" />
        <p className="text-gray-400 text-sm font-medium">Loading call history...</p>
      </div>
    );
  }

  if (callLogs.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 bg-[#1875FF]/10 rounded-xl flex items-center justify-center mx-auto mb-3">
          <Phone size={20} className="text-[#1875FF]" />
        </div>
        <p className="text-sm text-gray-500 font-medium">No call records yet</p>
        <p className="text-xs text-gray-400 mt-1">Call recordings and transcripts will appear here</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="text-[#1875FF]" size={16} />
        <span className="text-sm font-bold text-gray-900">
          Call Timeline ({callLogs.length} call{callLogs.length !== 1 ? 's' : ''})
        </span>
      </div>

      <div className="relative max-h-[500px] overflow-y-auto custom-scrollbar">
        <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#1875FF]/20 via-[#05E0D1]/15 to-transparent" />

        {callLogs.map((callLog, index) => {
          const hasRecording = callLog.call_recording && callLog.call_recording !== 'N/A';
          const hasTranscript = callLog.call_transcript && callLog.call_transcript !== '' && callLog.call_transcript !== 'N/A';

          return (
            <div key={index} className="relative flex items-start gap-4 pb-6">
              <div className="relative z-10 w-10 h-10 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                <Phone className="text-white" size={14} />
              </div>

              <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Clock className="text-gray-400" size={14} />
                      <span className="text-sm font-semibold text-gray-900">
                        {formatDateTime(callLog.created_at)}
                      </span>
                    </div>
                    {callLog.call_outcome && getCallOutcome(callLog.call_outcome)}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {callLog.call_duration && (
                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">Duration</span>
                        <div className="text-sm font-semibold text-gray-900">{callLog.call_duration}</div>
                      </div>
                    )}
                    {callLog.phone_number && (
                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">Phone</span>
                        <div className="text-sm font-semibold text-gray-900">{callLog.phone_number}</div>
                      </div>
                    )}
                    {callLog.call_type && (
                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">Type</span>
                        <div className="text-sm font-semibold text-gray-900">{callLog.call_type}</div>
                      </div>
                    )}
                    {callLog.agent_name && (
                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <span className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">Agent</span>
                        <div className="text-sm font-semibold text-gray-900">{callLog.agent_name}</div>
                      </div>
                    )}
                  </div>

                  {hasRecording && (
                    <div className="bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5 rounded-xl p-4 border border-[#05E0D1]/15 mb-3">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-6 h-6 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-lg flex items-center justify-center">
                          <Play className="text-white" size={10} />
                        </div>
                        <span className="text-xs font-semibold text-gray-700">Call Recording</span>
                      </div>
                      <audio controls className="w-full" preload="metadata" key={`rec-${leadId}-${index}`}>
                        <source src={callLog.call_recording} type="audio/mpeg" />
                        <source src={callLog.call_recording} type="audio/wav" />
                        <source src={callLog.call_recording} type="audio/mp3" />
                      </audio>
                    </div>
                  )}

                  {callLog.call_notes && callLog.call_notes !== 'N/A' && (
                    <div className="bg-[#05E0D1]/5 rounded-xl p-3 border border-[#05E0D1]/15 mb-3">
                      <div className="text-[10px] text-[#0bb5a5] font-semibold uppercase tracking-widest mb-1">Call Notes</div>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{callLog.call_notes}</div>
                    </div>
                  )}

                  {hasTranscript && (
                    <>
                      <button
                        onClick={() => toggleTranscript(index)}
                        className="flex items-center gap-2 text-[#1875FF] hover:text-[#1466E0] transition-colors font-semibold text-sm"
                      >
                        {expandedTranscripts.has(index) ? (
                          <><ChevronUp size={16} /><span>Hide Transcript</span></>
                        ) : (
                          <><ChevronDown size={16} /><span>Show Transcript</span></>
                        )}
                      </button>

                      {expandedTranscripts.has(index) && (
                        <div className="mt-3">
                          <div className="bg-gradient-to-r from-[#05E0D1]/5 to-[#1875FF]/5 rounded-xl p-4 border border-[#05E0D1]/15">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-6 h-6 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-lg flex items-center justify-center">
                                <FileText className="text-white" size={10} />
                              </div>
                              <span className="text-xs font-semibold text-gray-700">Call Transcript</span>
                            </div>
                            <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                              <div className="text-sm text-gray-700 whitespace-pre-wrap break-words leading-relaxed max-h-[300px] overflow-y-auto">
                                {callLog.call_transcript}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InlineCallTimeline;
